import React, { useState } from "react";
import PropTypes from "prop-types";
import Slider from "react-slick";
import styled from "styled-components";

// Ensure slick styles are loaded wherever this component is used
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const ModalImageWrapper = styled.div`
  position: relative;
  width: ${(p) => p.$containerWidth}px;
  height: ${(p) => p.$imageHeight}px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Allow arrows to extend horizontally while preventing vertical scroll */
  overflow-x: visible;
  overflow-y: hidden;
`;

const SliderWrapper = styled.div`
  position: relative;
  width: ${(p) => p.$imageWidth}px;
  height: ${(p) => p.$imageHeight}px;
`;

const SlideInner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const LargeImage = styled.img`
  width: ${(p) => p.$imageWidth}px;
  height: ${(p) => p.$imageHeight}px;
  object-fit: contain;
  display: block;
`;

const ArrowButton = styled.button`
  position: absolute;
  top: 50%;
  z-index: 2;
  background: ${(p) => (p.$transparent ? "transparent" : "rgba(255,255,255,0.9)")};
  border: ${(p) => (p.$transparent ? "none" : "1px solid #e0e0e0")};
  border-radius: 50%;
  width: ${(p) => p.$size || 40}px;
  height: ${(p) => p.$size || 40}px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${(p) => (p.$disabled ? "default" : "pointer")};
  opacity: ${(p) => (p.$disabled ? 0.3 : 1)};
  pointer-events: ${(p) => (p.$disabled ? "none" : "auto")};
  transform: translateY(-50%);
  padding: 0;
  &:hover {
    background: ${(p) => (p.$transparent ? "transparent" : "#fff")};
    box-shadow: ${(p) => (p.$transparent ? "none" : "0 2px 8px rgba(0,0,0,0.15)")};
  }
`;

function NextArrow(props) {
  const { onClick, className, style, offset = 60, size = 40, transparent } = props;
  return (
    <ArrowButton
      className={transparent ? className : ""}
      $size={size}
      $transparent={!!transparent}
      $disabled={!onClick}
      style={{ ...(style || {}), right: -offset }}
      onClick={onClick}
      aria-label="Next image"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
    </ArrowButton>
  );
}

function PrevArrow(props) {
  const { onClick, className, style, offset = 60, size = 40, transparent } = props;
  return (
    <ArrowButton
      className={transparent ? className : ""}
      $size={size}
      $transparent={!!transparent}
      $disabled={!onClick}
      style={{ ...(style || {}), left: -offset }}
      onClick={onClick}
      aria-label="Previous image"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
    </ArrowButton>
  );
}

const SlickImageCarousel = ({
  images,
  items={},
  currentIndex = 0,
  onChange,
  placeholderImage,
  renderErrorSlide,
  imageWidth = 441,
  imageHeight = 454,
  arrowOffset = 60,
  arrowSize = 40,
  arrowTransparent = false,
  // dynamic slider controls (override defaults if provided)
  dots,
  infinite,
  speed,
  slidesToShow,
  slidesToScroll,
  autoplay,
  autoplaySpeed,
  settings: userSettings = {},
}) => {
  const [erroredIndexes, setErroredIndexes] = useState(new Set());
  const containerWidth = imageWidth + arrowOffset * 2;

  const userAfterChange = userSettings.afterChange;
  const afterChange = (index) => {
    if (typeof userAfterChange === "function") userAfterChange(index);
    if (typeof onChange === "function") onChange(index);
  };

  const settings = {
    // allow any additional settings to come through
    ...userSettings,
    // apply coalesced values, with explicit props taking precedence over userSettings, then defaults
    dots: dots ?? userSettings.dots ?? false,
    infinite: infinite ?? userSettings.infinite ?? false,
    speed: speed ?? userSettings.speed ?? 300,
    slidesToShow: slidesToShow ?? userSettings.slidesToShow ?? 1,
    slidesToScroll: slidesToScroll ?? userSettings.slidesToScroll ?? 1,
    initialSlide: userSettings.initialSlide ?? currentIndex,
    arrows: userSettings.arrows ?? (images?.length > 1 || items?.length > 1),
    nextArrow: userSettings.nextArrow ?? (
      <NextArrow offset={arrowOffset} size={arrowSize} transparent={arrowTransparent} />
    ),
    prevArrow: userSettings.prevArrow ?? (
      <PrevArrow offset={arrowOffset} size={arrowSize} transparent={arrowTransparent} />
    ),
    autoplay: autoplay ?? userSettings.autoplay,
    autoplaySpeed: autoplaySpeed ?? userSettings.autoplaySpeed,
    afterChange,
  };

  if (items && Object.keys(items).length > 0) {
    return (
      <Slider {...settings}>
        {items.map((item, idx) => (
          <div key={idx}>{item}</div>
        ))}
      </Slider>
    );
  }
  return (
    <ModalImageWrapper $containerWidth={containerWidth} $imageHeight={imageHeight}>
      <SliderWrapper $imageWidth={imageWidth} $imageHeight={imageHeight}>
        <Slider {...settings}>
          {images.map((img, idx) => {
            const hasBlobUrl = !!img.url?.blobUrl;
            const isFailed = !hasBlobUrl || erroredIndexes.has(idx);
            return (
              <SlideInner key={img.imageId || idx}>
                {isFailed && renderErrorSlide ? (
                  renderErrorSlide(img, idx)
                ) : (
                  <LargeImage
                    $imageWidth={imageWidth}
                    $imageHeight={imageHeight}
                    src={img.url?.blobUrl || placeholderImage}
                    alt={`Preview ${idx + 1}`}
                    onError={() => setErroredIndexes((prev) => new Set([...prev, idx]))}
                  />
                )}
              </SlideInner>
            );
          })}
        </Slider>
      </SliderWrapper>
    </ModalImageWrapper>
  );
};

SlickImageCarousel.propTypes = {
  images: PropTypes.array,
  items: PropTypes.array,
  currentIndex: PropTypes.number,
  onChange: PropTypes.func,
  placeholderImage: PropTypes.string,
  renderErrorSlide: PropTypes.func,
  imageWidth: PropTypes.number,
  imageHeight: PropTypes.number,
  arrowOffset: PropTypes.number,
  arrowSize: PropTypes.number,
  arrowTransparent: PropTypes.bool,
  dots: PropTypes.bool,
  infinite: PropTypes.bool,
  speed: PropTypes.number,
  slidesToShow: PropTypes.number,
  slidesToScroll: PropTypes.number,
  autoplay: PropTypes.bool,
  autoplaySpeed: PropTypes.number,
  settings: PropTypes.object,
};

SlickImageCarousel.defaultProps = {
  images: [],
  items: [],
};

export default SlickImageCarousel;
